/**
 * Request Deduplication and Caching Utility
 *
 * Prevents redundant API calls by:
 * 1. Deduplicating identical concurrent requests
 * 2. Caching responses for a short period
 * 3. Debouncing rapid successive calls
 * 4. Providing request status tracking
 */

interface CacheEntry<T> {
  data: T;
  timestamp: number;
  expiresAt: number;
}

interface PendingRequest<T> {
  promise: Promise<T>;
  timestamp: number;
}

interface RequestOptions {
  cacheTTL?: number; // Cache time-to-live in milliseconds (default: 30 seconds)
  dedupeTTL?: number; // Deduplication time-to-live in milliseconds (default: 5 seconds)
  forceRefresh?: boolean; // Bypass cache and make fresh request
  debounceMs?: number; // Debounce delay in milliseconds (default: 0)
}

class CachedRequestManager {
  private cache = new Map<string, CacheEntry<any>>();
  private pendingRequests = new Map<string, PendingRequest<any>>();
  private debounceTimers = new Map<string, NodeJS.Timeout>();

  // Default configuration
  private readonly DEFAULT_CACHE_TTL = 30 * 1000; // 30 seconds
  private readonly DEFAULT_DEDUPE_TTL = 5 * 1000; // 5 seconds
  private readonly DEFAULT_DEBOUNCE_MS = 0; // No debouncing by default

  /**
   * Execute a request with caching and deduplication
   */
  async request<T>(
    cacheKey: string,
    requestFn: () => Promise<T>,
    options: RequestOptions = {}
  ): Promise<T> {
    const {
      cacheTTL = this.DEFAULT_CACHE_TTL,
      dedupeTTL = this.DEFAULT_DEDUPE_TTL,
      forceRefresh = false,
      debounceMs = this.DEFAULT_DEBOUNCE_MS,
    } = options;

    // Handle debouncing
    if (debounceMs > 0) {
      return this.debouncedRequest(cacheKey, requestFn, { ...options, debounceMs: 0 }, debounceMs);
    }

    const now = Date.now();

    // Cache debugging disabled for production

    // Check cache first (unless force refresh)
    if (!forceRefresh) {
      const cached = this.cache.get(cacheKey);
      if (cached && now < cached.expiresAt) {
        return cached.data;
      }
    }

    // Check if there's a pending request for the same cache key
    const pending = this.pendingRequests.get(cacheKey);
    if (pending && now - pending.timestamp < dedupeTTL) {
      return pending.promise;
    }

    const requestPromise = this.executeRequest(cacheKey, requestFn, cacheTTL);

    // Store as pending request
    this.pendingRequests.set(cacheKey, {
      promise: requestPromise,
      timestamp: now,
    });

    return requestPromise;
  }

  /**
   * Execute the actual request and handle caching
   */
  private async executeRequest<T>(
    cacheKey: string,
    requestFn: () => Promise<T>,
    cacheTTL: number
  ): Promise<T> {
    try {
      const result = await requestFn();
      const now = Date.now();

      // Cache the result
      this.cache.set(cacheKey, {
        data: result,
        timestamp: now,
        expiresAt: now + cacheTTL,
      });

      // Remove from pending requests
      this.pendingRequests.delete(cacheKey);

      return result;
    } catch (error) {
      // Remove from pending requests on error
      this.pendingRequests.delete(cacheKey);

      throw error;
    }
  }

  /**
   * Handle debounced requests
   */
  private debouncedRequest<T>(
    cacheKey: string,
    requestFn: () => Promise<T>,
    options: RequestOptions,
    debounceMs: number
  ): Promise<T> {
    return new Promise((resolve, reject) => {
      // Clear existing timer
      const existingTimer = this.debounceTimers.get(cacheKey);
      if (existingTimer) {
        clearTimeout(existingTimer);
      }

      // Set new timer
      const timer = setTimeout(async () => {
        try {
          this.debounceTimers.delete(cacheKey);
          const result = await this.request(cacheKey, requestFn, options);
          resolve(result);
        } catch (error) {
          reject(error);
        }
      }, debounceMs);

      this.debounceTimers.set(cacheKey, timer);
    });
  }

  /**
   * Clear cache entry
   */
  clearCache(cacheKey: string): void {
    this.cache.delete(cacheKey);
  }

  /**
   * Clear all cache entries
   */
  clearAllCache(): void {
    this.cache.clear();
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    totalEntries: number;
    validEntries: number;
    expiredEntries: number;
    pendingRequests: number;
  } {
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    Array.from(this.cache.values()).forEach((entry) => {
      if (now < entry.expiresAt) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    });

    return {
      totalEntries: this.cache.size,
      validEntries,
      expiredEntries,
      pendingRequests: this.pendingRequests.size,
    };
  }

  /**
   * Clean up expired cache entries
   */
  cleanup(): void {
    const now = Date.now();

    Array.from(this.cache.entries()).forEach(([cacheKey, entry]) => {
      if (now >= entry.expiresAt) {
        this.cache.delete(cacheKey);
      }
    });

    // Clean up old pending requests (older than 30 seconds)
    Array.from(this.pendingRequests.entries()).forEach(([cacheKey, pending]) => {
      if (now - pending.timestamp > 30000) {
        this.pendingRequests.delete(cacheKey);
      }
    });

    // Cleanup completed silently
  }
}

// Create singleton instance
export const cachedRequestManager = new CachedRequestManager();

// Convenience function for making cached requests
export const cachedRequest = <T>(
  cacheKey: string,
  requestFn: () => Promise<T>,
  options?: RequestOptions
): Promise<T> => {
  return cachedRequestManager.request(cacheKey, requestFn, options);
};

// Export cache invalidation function
export const invalidateCache = (cacheKey: string): void => {
  cachedRequestManager.clearCache(cacheKey);
};

// Auto-cleanup every 5 minutes
if (typeof window !== 'undefined') {
  setInterval(
    () => {
      cachedRequestManager.cleanup();
    },
    5 * 60 * 1000
  );
}

export default cachedRequest;
